/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Directory OpenSearch parameter building and result pagination helpers.
 * @module logic/directory/directorySearch
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import {
  fetchCompaniesFromOpenSearch,
  preloadTheRemainingCompanies,
} from "./index.js";
import { EMPTY_DIRECTORY_FILTERS } from "./directoryInit.js";

export const buildDirectorySearchParameters = ({
  localFilters,
  localSearchQuery,
  searchQuery,
  filters,
  searchType,
}) => {
  const query = (localSearchQuery ?? searchQuery).trim();
  const baseFilters =
    localFilters === null || localFilters === undefined
      ? filters
      : localFilters;

  const searchParameters = {
    searchQuery: query,
    ...baseFilters,
  };

  if (searchType === "product") {
    return {
      ...searchParameters,
      searchType: "product",
    };
  }

  return searchParameters;
};

export const isEmptyDirectorySearch = (searchParameters) =>
  !searchParameters.searchQuery &&
  searchParameters.sector === null &&
  searchParameters.subSector === null &&
  searchParameters.rama === null &&
  searchParameters.tamanoDeLaEmpresa === null &&
  searchParameters.estado === null &&
  searchParameters.municipio === null &&
  searchParameters.exporta === null &&
  searchParameters.importa === null;

export const executeDirectorySearch = async ({
  navigate,
  searchParameters,
  originalCompaniesData,
  setters,
}) => {
  setters.setLastSort(null);
  setters.setLimitReached(false);
  setters.setCurrentPage(1);
  setters.setLoading(true);

  try {
    const searchResults = await fetchCompaniesFromOpenSearch(
      navigate,
      searchParameters,
      null,
      null,
    );
    const firstPageItems = Array.isArray(searchResults?.items)
      ? searchResults.items
      : [];

    setters.setTotalResults(searchResults?.total ?? 0);
    setters.setFilteredCompanyData(firstPageItems);

    if (searchResults?.lastSort) {
      preloadTheRemainingCompanies(
        navigate,
        searchParameters,
        searchResults.lastSort,
        firstPageItems,
        setters.setFilteredCompanyData,
        setters.setLastSort,
        setters.setLimitReached,
      );
    } else {
      setters.setLimitReached(true);
      setters.setLastSort(null);
    }
  } catch (error) {
    logger.error("Error searching companies:", { err: sanitizeError(error) });
    setters.setFilteredCompanyData(originalCompaniesData || []);
    setters.setTotalResults(originalCompaniesData.length || 0);
  } finally {
    setters.setLoading(false);
  }
};

export const resetDirectorySearchResults = ({
  originalCompaniesData,
  setters,
}) => {
  setters.setCurrentPage(1);
  setters.setLastSort(null);
  setters.setFilteredCompanyData(originalCompaniesData || []);
  setters.setTotalResults(originalCompaniesData.length || 0);
};

export { EMPTY_DIRECTORY_FILTERS };
